import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@muzly/ui-kit';

import { BreathSection } from './BreathSection';
import { FatigueSection } from './FatigueSection';
import { PitchRegisterSection } from './PitchRegisterSection';
import { RangeSection } from './RangeSection';
import { ResonanceStyleSection } from './ResonanceStyleSection';
import { SingersFormantSection } from './SingersFormantSection';
import { VibratoSection } from './VibratoSection';
import { AlertCircleIcon, ScoreHero } from '../../../../components';
import { tabInsight } from '../../resultCopy';

import type { DiagnosisRecord } from '../../record';
import type { MetricTabId } from '../../resultCopy';

/**
 * 지표 상세의 공통 셸 — 탭 바 · 탭 머리(제목·부제·큰 점수) · 본문 · 한 줄 해석 · 각주
 * (시안 §8.2).
 *
 * 지표를 쌓지 않고 탭으로 미는 이유: 한 지표를 볼 때 다른 지표의 숫자가 같이 보이면
 * 사람은 그것들을 서로 견준다. 그런데 이 값들은 **서로 다른 자로 잰 것**이라(호흡은 반음
 * 대비 편차, 음역 구간은 이웃 표본 간격의 퍼짐) 82와 71을 나란히 두면 없는 비교가 생긴다.
 *
 * **탭 일곱이 시안 §8.2의 전부다**(목소리 피로도·음역·음정·바이브레이션·호흡 안정도·
 * 싱어즈 포먼트·공명 구간). 값이 없는 탭도 선다 — 안 서면 그런 축이 있다는 사실이
 * 화면에서 사라지고, 다음 회차를 잴 이유도 같이 사라진다. 대신 값 자리는 전부
 * `측정 준비 중`이다(0을 쓰지 않는다).
 *
 * 어느 탭을 세울지는 **호출부가 정한다**(`only`). 진단 직후의 결과 본문은 음역을 자기
 * 섹션으로 이미 그리고 있어 그 탭을 빼고, `/result/:id/:tab` 상세는 일곱을 다 세운다.
 */

/**
 * 탭 하나가 그리는 것. `Record<MetricTabId, …>`인 이유: 지표를 하나 더하면
 * (`METRIC_TAB_IDS`) **여기서 컴파일이 깨진다** — 목록에만 있고 그릴 것이 없는 탭이
 * 조용히 서는 일을 타입이 막는다.
 */
const TAB_RENDER: Record<
  MetricTabId,
  (r: DiagnosisRecord, previous: DiagnosisRecord | null) => ReactElement
> = {
  fatigue: () => <FatigueSection />,
  range: (record) => <RangeSection record={record} />,
  registers: (record, previous) => (
    <PitchRegisterSection record={record} previous={previous} />
  ),
  vibrato: (record) => <VibratoSection record={record} />,
  breath: (record) => <BreathSection record={record} />,
  formant: (record) => <SingersFormantSection record={record} />,
  resonance: (record) => <ResonanceStyleSection record={record} />,
};

/**
 * 탭 머리의 큰 점수(시안 `87 / 100`). **점수 축이 있는 탭만** 낸다 — 음역·싱어즈 포먼트·
 * 공명 구간은 사실(음이름·Hz)이지 점수가 아니라 자리를 비운다(`undefined`). 피로도는 축은
 * 있는데 아직 못 재므로 `null` → `측정 준비 중`. 호흡은 홈 타일과 같은 축(`sustain`)이다.
 */
const TAB_SCORE: Record<
  MetricTabId,
  (r: DiagnosisRecord) => number | null | undefined
> = {
  fatigue: () => null,
  range: () => undefined,
  registers: (r) =>
    r.scores.find((s) => s.key === 'pitchAccuracy')?.val ?? null,
  vibrato: (r) => r.scores.find((s) => s.key === 'vibrato')?.val ?? null,
  breath: (r) => r.scores.find((s) => s.key === 'sustain')?.val ?? null,
  formant: () => undefined,
  resonance: () => undefined,
};

/**
 * 화면에 서는 순서 — 시안 §8.2의 탭 바 그대로다. 홈 타일(`homeTiles`)은 **다른 순서**를
 * 쓴다: 시안 §7.1이 `음정`을 `음역`보다 앞에 둔다. 순서를 공유 상수로 굳히면 한 화면이
 * 시안과 어긋나므로 각자 갖는다.
 */
const TAB_ORDER: MetricTabId[] = [
  'fatigue',
  'range',
  'registers',
  'vibrato',
  'breath',
  'formant',
  'resonance',
];

/** 한 줄 해석 카드 — 시안 §8.2의 연분홍 카드(제목 18 + 핑크 본문). 할 말이 없으면 안 그린다. */
const InsightCard = ({
  record,
  tab,
}: {
  record: DiagnosisRecord;
  tab: MetricTabId;
}) => {
  const { t } = useTranslation();
  const key = tabInsight(record, tab);
  if (key === null) {
    return null;
  }
  return (
    <div className="mx-[18px] rounded-[24px] bg-[rgba(251,35,59,0.03)] px-5 py-[18px]">
      <h3 className="text-lg font-semibold tracking-[-0.09px] text-bk-900">
        {t('web.result.tabs.insightTitle')}
      </h3>
      <p className="mt-2 text-sm leading-[1.35] tracking-[-0.07px] text-pink">
        {t(`web.result.tabs.insight.${tab}.${key}`)}
      </p>
    </div>
  );
};

export const ResultTabs = ({
  record,
  previous,
  only,
  tab,
  onTabChange,
}: {
  record: DiagnosisRecord;
  /** 이력 대비의 기준선(직전 회차). 첫 회차에는 null. */
  previous: DiagnosisRecord | null;
  /** 세울 탭 목록. 안 주면 시안의 일곱을 다 세운다. */
  only?: MetricTabId[];
  /**
   * 지금 탭 — 주면 **주소가 정본**이 된다(`/result/:id/:tab`). 안 주면 내부 상태로 돈다.
   * 두 경로를 한 컴포넌트가 갖는 이유: 탭 바·해석 카드·각주가 같은 셸이라, 갈라 놓으면
   * 시안 §8.2가 두 벌이 된다.
   */
  tab?: MetricTabId;
  onTabChange?: (id: MetricTabId) => void;
}) => {
  const { t } = useTranslation();
  const tabs = only ? TAB_ORDER.filter((id) => only.includes(id)) : TAB_ORDER;
  const [active, setActive] = useState<MetricTabId | null>(null);
  // 모르는 탭 이름(주소를 손으로 고쳤다)은 첫 탭으로 떨어진다 — 빈 화면을 보이지 않는다.
  const current = tabs.find((id) => id === (tab ?? active)) ?? tabs[0];
  const score = TAB_SCORE[current](record);

  return (
    <section className="flex flex-col gap-4">
      {/* 탭 바는 ui-kit(Radix)이 그린다 — 화살표 키 이동·roving tabindex·`aria-controls`가
          손으로 짠 `role="tab"` 버튼에는 없었다. 시안 §8.2의 모양(라운드 10, 활성만 연분홍
          바탕 + 레드 글자, 비활성은 바탕 없이 회색 글자)은 className으로 입힌다. */}
      <Tabs
        value={current}
        onValueChange={(id) => {
          setActive(id as MetricTabId);
          onTabChange?.(id as MetricTabId);
        }}
        className="flex flex-col gap-2"
      >
        {/* 가로 스크롤 — 탭이 늘어도 줄바꿈이 아니라 옆으로 흐른다(시안 §8.2). */}
        <TabsList
          aria-label={t('web.result.tabs.label')}
          className="h-auto justify-start gap-0 overflow-x-auto rounded-none bg-transparent p-0 px-[18px] py-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {tabs.map((id) => (
            <TabsTrigger
              key={id}
              value={id}
              className="shrink-0 rounded-[10px] px-3 py-2.5 text-[15px] font-medium tracking-[-0.075px] text-[#9ea1a6] transition-colors data-[state=active]:bg-[rgba(251,35,59,0.04)] data-[state=active]:font-semibold data-[state=active]:text-primary data-[state=active]:shadow-none"
            >
              {t(`web.result.tabs.name.${id}`)}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* 탭 머리 — 제목(24) · 부제(14) · 큰 점수. 탭마다 같은 틀이라 본문 밖에서 한 번 그린다. */}
        <div className="flex flex-col">
          <div className="flex flex-col gap-2 px-[18px] py-3">
            <h2 className="text-2xl font-semibold tracking-[-0.12px] text-black">
              {t(`web.result.tabs.name.${current}`)}
            </h2>
            <p className="text-sm font-medium leading-normal tracking-[-0.07px] text-bk-700">
              {t(`web.result.tabs.subtitle.${current}`)}
            </p>
          </div>
          {score !== undefined && (
            <div className="px-[18px] py-3.5">
              <ScoreHero score={score} />
            </div>
          )}
        </div>

        {tabs.map((id) => (
          <TabsContent key={id} value={id} className="mt-0">
            {TAB_RENDER[id](record, previous)}
          </TabsContent>
        ))}
      </Tabs>

      <InsightCard record={record} tab={current} />

      <div className="flex flex-col items-center gap-3 px-[18px] py-2.5">
        <AlertCircleIcon className="h-6 w-6 text-bk-400" aria-hidden="true" />
        <p className="text-center text-sm tracking-[-0.07px] text-bk-500">
          {t('web.result.tabs.footnote')}
        </p>
      </div>
    </section>
  );
};
